import { Typography, Box, Drawer, IconButton, Paper, Tooltip } from '@mui/material';
import { MessagesSquare } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { fundSubmissionsQuery, getPreviousPeriod, useAgentRoster, useAgentRuns, useFunds, usePlanApprovals, useTenant } from '@diggens/shared-hooks';
import { useCurrentUser } from '@diggens/user-management';
import { CONVERSATIONS_QUERY_KEY, ChatClient, ConversationList, runOutcome, useConversations, type ChatClientRef } from '@diggens/chat';
import { examplePrompts } from '../components/chat/examplePrompts';
import { slashCommands } from '../components/chat/slashCommands';
import { buildStarters, summarizeCollection, type Starter } from '../components/chat/starters';
import { FormModal } from '../components/chat/FormModal';
import { SubmissionStatusPanel } from '../components/chat/SubmissionStatusPanel';
import type { ChatAction } from '@diggens/types';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';
import { MY_POLICIES_QUERY_KEY } from './settings/AgentPreferences';

export function AdminDashboard() {
  const location = useLocation();
  const { data: user } = useCurrentUser();
  const { data: tenant } = useTenant(user?.tenant_id || '');
  const { data: funds, isPending: fundsPending } = useFunds(user?.tenant_id || undefined);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // What the starters and slash commands draw on: runs and submissions are read once when the page opens, and the
  // approvals refresh with the nav's count
  const { data: approvals, isPending: approvalsPending } = usePlanApprovals({ poll: false });
  const { data: runs, isPending: runsPending } = useAgentRuns({ poll: false });
  const { data: roster } = useAgentRoster();
  const { data: conversations, isPending: conversationsPending } = useConversations();
  const scheduled = (funds ?? []).filter((fund) => fund.reporting_frequency);
  const periodOf = (fund: (typeof scheduled)[number]) => getPreviousPeriod(fund.reporting_frequency!, fund.reporting_timezone || 'UTC');
  const period = scheduled[0] ? periodOf(scheduled[0]) : null;
  const submissions = useQueries({ queries: scheduled.filter((fund) => periodOf(fund) === period).map((fund) => fundSubmissionsQuery(fund.id, period!)) });
  const collection =
    period && submissions.length > 0 && submissions.every((query) => query.data)
      ? summarizeCollection(
          period,
          submissions.flatMap((query) => query.data!)
        )
      : null;
  const starters = buildStarters({
    viewerId: user?.id ?? null,
    pendingPlans: approvals?.pending ?? [],
    failedRuns: (runs ?? []).filter((run) => runOutcome(run) === 'failed'),
    collection,
    lastConversation: conversations?.[0] ?? null,
    examples: examplePrompts(funds ?? []),
  });
  // Until what the starters draw on has loaded, they would offer generic prompts in place of the workspace's own
  const startersLoading = fundsPending || approvalsPending || runsPending || conversationsPending || submissions.some((query) => query.isPending);
  const commands = slashCommands({ period, specialists: new Set((roster ?? []).filter((entry) => entry.enabled).map((entry) => entry.agent)) });
  const [currentAction, setCurrentAction] = useState<ChatAction | null>(null);
  // The conversation on screen, which the list beside the chat highlights; null while it is new
  const [conversationId, setConversationId] = useState<string | null>(null);
  const chatClientRef = useRef<ChatClientRef>(null);
  // Drops the navigation's state through the router, so a reload or Back does not deliver it again; replacing the
  // browser's history state directly would also drop the router's own index and key, and break Back and Forward
  const clearNavigationState = () => navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
  // A prompt from the nav, like New fund, is sent once, as the user
  const initialPrompt: string | undefined = typeof location.state?.prompt === 'string' ? location.state.prompt : undefined;
  // Cleared once the chat is on screen: it sends the prompt as it mounts, which comes first
  const chatShown = !!user && !!tenant;
  useEffect(() => {
    if (initialPrompt && chatShown) clearNavigationState();
  }, [location.key, initialPrompt, chatShown]);
  // A navigation that brings a prompt, like New fund in the nav, starts its own conversation; opening the Chief of Staff
  // without one keeps the conversation on screen
  const [chatKey, setChatKey] = useState(initialPrompt ? location.key : 'chat');
  if (initialPrompt && chatKey !== location.key) {
    setChatKey(location.key);
    setConversationId(null);
    // A form left open from the conversation being replaced would notify the new one
    setCurrentAction(null);
  }

  // A saved reply may have made a new conversation or titled one, so the list refetches, and may have saved a
  // preference the person taught it; opening one changes nothing
  const handleReplySaved = () => {
    queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: MY_POLICIES_QUERY_KEY });
  };
  // The list in a drawer, for widths where it does not fit beside the chat and while a form panel takes the room
  const [conversationsOpen, setConversationsOpen] = useState(false);
  const openConversation = (id: string) => {
    setCurrentAction(null);
    setConversationsOpen(false);
    chatClientRef.current?.loadConversation(id);
  };
  const startNewConversation = () => {
    setCurrentAction(null);
    setConversationsOpen(false);
    chatClientRef.current?.startNewConversation();
  };
  // Starters that do not send a message: one continues a conversation, one goes to the plan waiting for a decision in
  // Activity
  const handleStarterPicked = (starter: Starter) => {
    if (starter.action.kind === 'open') openConversation(starter.action.conversationId);
    else if (starter.action.kind === 'review') navigate(`/activity?plan=${encodeURIComponent(starter.action.planId)}`);
    else if (starter.action.kind === 'run') navigate(`/activity?run=${encodeURIComponent(starter.action.runId)}`);
  };

  // Handle initial message from navigation state
  useEffect(() => {
    if (location.state?.message && chatClientRef.current) {
      chatClientRef.current.notifyAgent(location.state.message);
      // Clear the state to prevent re-sending on re-render
      clearNavigationState();
    }
  }, [location.state]);

  if (!user || !tenant) {
    return <Typography>Loading...</Typography>;
  }

  const drawerWidth = '50%';
  const drawerOpen = currentAction?.type === 'show_form' || currentAction?.type === 'show_status';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box sx={{ display: 'flex', gap: 2, flex: 1, overflow: 'hidden', p: 3 }}>
        {/* Past conversations beside the chat from 1200px; hidden while a form or status panel needs the room */}
        {!drawerOpen && (
          <Paper sx={{ width: 260, flexShrink: 0, display: { xs: 'none', lg: 'flex' }, flexDirection: 'column', overflow: 'hidden' }}>
            <ConversationList currentConversationId={conversationId} onSelect={openConversation} onNew={startNewConversation} />
          </Paper>
        )}
        <Drawer anchor="right" open={conversationsOpen} onClose={() => setConversationsOpen(false)}>
          <Box sx={{ width: 320, height: '100%' }}>
            <ConversationList currentConversationId={conversationId} onSelect={openConversation} onNew={startNewConversation} />
          </Box>
        </Drawer>

        {/* Manager Agent Chat */}
        <Paper
          sx={{
            p: 3,
            flex: 1,
            minWidth: 0,
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ChatClient
            ref={chatClientRef}
            tenantId={user.tenant_id || ''}
            userId={user.id}
            userName={(user.full_name || user.email || 'User') as string}
            onActionTriggered={setCurrentAction}
            initialPrompt={initialPrompt}
            starters={starters}
            startersLoading={startersLoading}
            onStarterPicked={handleStarterPicked}
            slashCommands={commands}
            onConversationChange={setConversationId}
            onReplySaved={handleReplySaved}
            onPreferencesChanged={() => queryClient.invalidateQueries({ queryKey: MY_POLICIES_QUERY_KEY })}
            headerAction={
              <Tooltip title="Conversations">
                <IconButton size="small" aria-label="Conversations" onClick={() => setConversationsOpen(true)} sx={{ display: drawerOpen ? 'inline-flex' : { xs: 'inline-flex', lg: 'none' } }}>
                  <MessagesSquare size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
                </IconButton>
              </Tooltip>
            }
            key={chatKey}
          />
        </Paper>

        {/* Side Panel (not a Drawer) */}
        {drawerOpen && (
          <Paper
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              transition: 'all 0.3s ease',
              overflow: 'hidden',
            }}
          >
            {currentAction?.type === 'show_form' && (
              <FormModal
                key={`${currentAction.form_id}-${Date.now()}`}
                formId={currentAction.form_id!}
                data={{
                  ...currentAction.data,
                  ...(currentAction.form_id === 'upload_document' && {
                    file: chatClientRef.current?.getUploadedFile(),
                  }),
                }}
                tenantId={user.tenant_id || ''}
                onSuccess={(result: any) => {
                  // Handle form transitions (e.g., trial_processing -> trial_results)
                  if (result.action === 'show_results' && result.data) {
                    setCurrentAction({
                      type: 'show_form',
                      form_id: 'trial_results',
                      data: result.data,
                    });
                    return; // Don't close the form, just transition
                  }

                  // Notify agent based on form type
                  if (currentAction.form_id === 'define_metrics') {
                    chatClientRef.current?.notifyAgent(`I just saved ${result} metric definitions for the fund.`);
                  } else if (currentAction.form_id === 'add_fund') {
                    chatClientRef.current?.notifyAgent(`I just created a new fund. What should I do next?`);
                  } else if (currentAction.form_id === 'import_companies') {
                    chatClientRef.current?.notifyAgent(`I just imported ${result.companies} companies and ${result.contacts} contacts.`);
                  } else if (currentAction.form_id === 'upload_document') {
                    const link = `/funds/${result.fundId}?submission=${result.submissionId}`;
                    chatClientRef.current?.notifyAgent(`Processing complete for ${result.companyName} ${result.period}. [View submission](${link})`);
                  } else if (currentAction.form_id === 'trial_upload') {
                    // Notify agent with uploaded file metadata from trial
                    const fileMetadata = {
                      fileName: result.uploadedFile.fileName,
                      filePath: result.uploadedFile.filePath,
                      fileSize: result.uploadedFile.fileSize,
                      mimeType: result.uploadedFile.mimeType,
                    };
                    chatClientRef.current?.notifyAgent(`I uploaded a trial document: ${result.uploadedFile.fileName}`, fileMetadata);
                  } else if (currentAction.form_id === 'trial_confirmation' || currentAction.form_id === 'show_trial_confirmation') {
                    // User confirmed trial extraction
                    chatClientRef.current?.notifyAgent(`Run extraction for submission ${result.submissionId}`);
                  } else if (currentAction.form_id === 'trial_results') {
                    // User wants to set up their fund after seeing trial results
                    if (result.action === 'setup_fund') {
                      chatClientRef.current?.notifyAgent('I want to set up my fund');
                    }
                  } else if (currentAction.form_id === 'trial_processing') {
                    // Processing completed, form already transitioned to results
                    // Do nothing - the form already handled the transition
                    return;
                  }
                  setCurrentAction(null);
                }}
                onClose={() => setCurrentAction(null)}
              />
            )}
            {currentAction?.type === 'show_status' && (
              <SubmissionStatusPanel
                fundName={currentAction.data?.fund_name || 'Fund'}
                period={currentAction.data?.period || ''}
                submissions={currentAction.data?.submissions || []}
                onClose={() => setCurrentAction(null)}
              />
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
}
